import { message } from "antd";
import axios from "axios";

import { ROUTES_URL, isLocalhost } from "@/constant";
import { useAuthStore } from "@/stores/authStore";
import { useTenantStore } from "@/stores/tenantStore";
import { ApiStatusCheck } from "@/utils";
import { restoreFileReferences, transformUrlFields } from "@/utils/fileBlobReferenceUtils";

let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

const PledgeAPI = axios.create({
  // baseURL: `${process.env.AUTH_API_BASE_URL}/pledge`,

  headers: {
    "Content-Type": "application/json",
  },
});

PledgeAPI.interceptors.request.use(async (config) => {
  const token = useAuthStore.getState().token;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  const backendApiUrl = await useTenantStore.getState().getBackendApiUrl();
  config.baseURL = `${backendApiUrl}/api/pledge`;

  const tenantDomain = isLocalhost
    ? new URL(String(process.env.AUTH_API_BASE_URL)).hostname
    : window.location.host;

  config.headers["X-Tenant-Domain"] = tenantDomain;

  if (config.data && !(config.data instanceof FormData)) {
    config.data = restoreFileReferences(config.data);
  }

  if (config.params) {
    config.params = restoreFileReferences(config.params);
  }

  return config;
});

// Global error handling
PledgeAPI.interceptors.response.use(
  async (response) => {
    const contentType = response.headers["content-type"] ?? "";

    if (contentType.includes("application/json")) {
      response.data = await transformUrlFields(response.data);
    }

    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    const refreshToken = useAuthStore.getState().refresh_token;

    if (error.response?.status === 401 && !originalRequest._retry && refreshToken) {
      originalRequest._retry = true;

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve,
            reject,
          });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;

          return PledgeAPI(originalRequest);
        });
      }

      isRefreshing = true;

      try {
        const newAccessToken = await useAuthStore.getState().refreshTokenIfNeeded();

        processQueue(null, newAccessToken);

        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

        return PledgeAPI(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);

        useAuthStore.getState().logout();

        window.location.href = ROUTES_URL.LOGIN;

        throw refreshError;
      } finally {
        isRefreshing = false;
      }
    } else {
      const { response } = error;
      if (response) {
        const { status, data } = response;

        ApiStatusCheck(status, data?.message);
      } else {
        console.error("Network Error:", error.message);
      }
    }

    throw error;
  },
);

export default PledgeAPI;
